import { Card } from "@/components/layout/Card";
import { Carousel as CarouselComponent } from "@/components/layout/Carousel";
import { storyblokEditable } from "@storyblok/react/rsc";

import type { AssetStoryblok, CarouselStoryblok } from "@/component-types-sb";
import { BodyText, Divider, H2 } from "@/components/typography";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";

type CarouselPops = {
  blok: CarouselStoryblok;
};

type CarouselItem = Exclude<
  NonNullable<CarouselStoryblok["items"]>[number],
  string
>;

type SyntheticCarouselItem = {
  id: string;
  full_slug: string;
  name: string;
  content: {
    image: AssetStoryblok;
    lead: string;
    is_new: boolean;
  };
};

const Carousel = ({ blok }: CarouselPops) => {
  const lg = useMediaQuery("(min-width: 1024px)");
  const md = useMediaQuery("(min-width: 768px)");

  const mapTile: SyntheticCarouselItem = {
    id: "deutschlandkarte",
    full_slug: "deutschlandkarte",
    name: "Deutschlandkarte",
    content: {
      image: {
        id: 0,
        filename: "/deutschlandkarte.webp",
        name: "deutschlandkarte.webp",
        alt: "Deutschlandkarte",
      },
      lead: "Alle Standorte auf einen Blick",
      is_new: false,
    },
  };

  const items: (CarouselItem | SyntheticCarouselItem)[] = [
    mapTile,
    ...((blok.items ?? []).filter(
      (item): item is CarouselItem => typeof item !== "string"
    ) as CarouselItem[]),
  ];

  return (
    <section className="py-8 text-center" {...storyblokEditable(blok)}>
      <H2 colorScheme="primary">{blok.headline}</H2>
      <BodyText className="max-w-md mx-auto mt-4 text-center">
        {blok.text}
      </BodyText>

      <Divider />
      <div className="mb-10 md:mx-24 text-left">
        <CarouselComponent
          className="basis-full md:basis-1/2 lg:basis-1/3 lg mr-3"
          indicator
          options={{
            align: "start",
            slidesToScroll: lg ? 3 : md ? 2 : 1,
            loop: true,
          }}
        >
          {items.map((nestedBlok) => (
            <Card
              key={nestedBlok.id}
              image={nestedBlok.content?.image}
              href={nestedBlok.full_slug}
              name={nestedBlok.name}
              text={nestedBlok.content?.lead}
              isNew={nestedBlok.content.is_new}
              size="tall"
            />
          ))}
        </CarouselComponent>
      </div>
    </section>
  );
};

Carousel.blockName = "carousel";

export default Carousel;
